// ============================================================================
// FSA — "Re-round all" (engagement-level bulk re-round)
// ============================================================================
// After the engagement's rounding factor changes mid-way, re-round every
// saved Note and Statement under the new factor, in dependency order:
//
//   1. Notes that read nothing from a Statement      → reset + save
//   2. Statements those dependent notes read (P&L)    → refresh from Notes + save
//   3. Notes that read a Statement (Reserves profit line, EPS profit,
//      value-source disclosure rows)                   → reset + re-pull + save
//   4. Every financial statement in display order
//      (P&L again for its EPS lines, Balance Sheet, …, Cash Flow last)
//
// The per-note rule is the Note Modal's own Reset rounding
// (notes/shared/resetNoteRounding) — one rule, two entry points. It only
// re-rounds: no TB / ledger / cross-note pulls, so no other figure moves.
// Saving goes through the normal save endpoints (server recomputes totals,
// EPS, snapshots, factor-exempt classes, CF workings).
//
// Skipped (and listed): submitted / approved, excluded, never saved, and
// text-only (POLICY) notes. Statement overrides are kept and listed.
// Idempotent: re-running re-applies the same result, so a run interrupted
// by a failure is resumed by running the remaining items again.
//
// Pure orchestration — services are injected (tests use fakes).

import type { ID } from 'api/types';
import type { NoteHydrate, NoteListItem, NoteWorkspaceData } from 'api/types/fsa/noteData.types';
import type { StatementHydrate, StatementListItem, StatementWorkspaceData } from 'api/types/fsa/statementData.types';
import { refreshPlFlowLines, resetNoteRounding } from 'components/modals/modules/fsa/notes/shared/resetNoteRounding';
import {
  buildValueSourceRefresh,
  splitLiveValuesByDisclosure,
  type ValueSourceSpec,
} from 'components/modals/modules/fsa/notes/panes/disclosures/valueSourceSync';
import { refreshStatementFromLive } from '../statementLines';

export interface ReroundDeps {
  listNotes(engagementId: ID): Promise<NoteListItem[]>;
  hydrateNote(engagementId: ID, scheduleId: ID): Promise<NoteHydrate>;
  saveNote(engagementId: ID, scheduleId: ID, req: { rowVersion?: number | null; data: string }): Promise<unknown>;
  listStatements(engagementId: ID): Promise<StatementListItem[]>;
  hydrateStatement(engagementId: ID, reportId: ID): Promise<StatementHydrate>;
  saveStatement(engagementId: ID, reportId: ID, req: { rowVersion?: number | null; data: string }): Promise<unknown>;
}

export type ReroundStatus = 'pending' | 'running' | 'done' | 'skipped' | 'failed';

export interface ReroundItem {
  /** Unique within a plan (a Statement can appear twice: phase 2 and 4). */
  key: string;
  kind: 'note' | 'statement';
  id: ID;
  code: string;
  name: string;
  phase: 1 | 2 | 3 | 4;
  status: ReroundStatus;
  /** Why skipped / why failed. */
  reason?: string;
  /** Statement lines the auditor overrode — kept as typed, listed for review. */
  keptOverrides?: string[];
}

const LOCKED = new Set(['SUBMITTED', 'APPROVED']);

/** TRUE when the note reads a rounded figure from a Statement. */
export function readsStatement(h: NoteHydrate): boolean {
  if ((h.inboundPlFlows ?? []).some(f => f.distribution === 'SINGLE')) return true;
  if (Object.keys(h.valueSourceLiveValues ?? {}).length > 0) return true;
  return (h.liveClasses ?? []).some(c => c.isComputed && /"STATEMENT_VALUE"/i.test(c.computationRuleJson ?? ''));
}

/** Report codes a dependent note reads from. */
function statementsReadBy(h: NoteHydrate): string[] {
  const codes = new Set<string>();
  for (const f of h.inboundPlFlows ?? []) if (f.distribution === 'SINGLE') codes.add(f.sourceReportCode);
  for (const c of h.liveClasses ?? []) {
    if (!c.isComputed || !c.computationRuleJson) continue;
    try {
      const r = JSON.parse(c.computationRuleJson) as { type?: string; report_code?: string };
      if (r.type?.toUpperCase() === 'STATEMENT_VALUE' && r.report_code) codes.add(r.report_code);
    } catch { /* ignore */ }
  }
  try {
    const specs = JSON.parse(h.additionalDisclosuresSpecJson ?? '[]') as Array<{ fixed_rows?: Array<{ value_source?: { report_code?: string } }> }>;
    for (const s of Array.isArray(specs) ? specs : []) {
      for (const fr of s.fixed_rows ?? []) if (fr.value_source?.report_code) codes.add(fr.value_source.report_code);
    }
  } catch { /* ignore */ }
  return Array.from(codes);
}

/**
 * Builds the run list. Hydrates every saved note once to tell the
 * statement-dependent ones apart (the run re-hydrates before saving, so the
 * values it writes are always current).
 */
export async function planReround(engagementId: ID, deps: ReroundDeps): Promise<ReroundItem[]> {
  const [notes, statements] = await Promise.all([deps.listNotes(engagementId), deps.listStatements(engagementId)]);
  const independent: ReroundItem[] = [];
  const dependent: ReroundItem[] = [];
  const skipped: ReroundItem[] = [];
  const readCodes = new Set<string>();

  const sortedNotes = [...notes].sort((a, b) => a.displayOrder - b.displayOrder);
  for (const n of sortedNotes) {
    const base = { kind: 'note' as const, id: n.scheduleId, code: n.scheduleCode, name: n.scheduleName };
    const skipReason = !n.hasSavedData ? 'Never saved'
      : n.isExcluded ? 'Excluded (not applicable)'
      : LOCKED.has(n.statusCode) ? `${n.statusCode.toLowerCase()} — reopen to re-round`
      : null;
    if (skipReason) { skipped.push({ ...base, key: `n:${n.scheduleId}`, phase: 1, status: 'skipped', reason: skipReason }); continue; }
    const h = await deps.hydrateNote(engagementId, n.scheduleId);
    if (h.formatType === 'POLICY') {
      skipped.push({ ...base, key: `n:${n.scheduleId}`, phase: 1, status: 'skipped', reason: 'Text note — no amounts' });
      continue;
    }
    if (readsStatement(h)) {
      statementsReadBy(h).forEach(c => readCodes.add(c.toUpperCase()));
      dependent.push({ ...base, key: `n:${n.scheduleId}`, phase: 3, status: 'pending' });
    } else {
      independent.push({ ...base, key: `n:${n.scheduleId}`, phase: 1, status: 'pending' });
    }
  }

  const fs = [...statements]
    .filter(s => s.reportType === 'FINANCIAL_STATEMENT')
    .sort((a, b) => a.displayOrder - b.displayOrder);
  const sources: ReroundItem[] = [];
  const finals: ReroundItem[] = [];
  for (const s of fs) {
    const base = { kind: 'statement' as const, id: s.reportId, code: s.reportCode, name: s.reportName };
    const skipReason = !s.hasSavedData ? 'Never saved'
      : s.isExcluded ? 'Excluded (not applicable)'
      : LOCKED.has(s.statusCode) ? `${s.statusCode.toLowerCase()} — reopen to re-round`
      : null;
    if (skipReason) { skipped.push({ ...base, key: `s4:${s.reportId}`, phase: 4, status: 'skipped', reason: skipReason }); continue; }
    if (dependent.length > 0 && readCodes.has(s.reportCode.toUpperCase())) {
      sources.push({ ...base, key: `s2:${s.reportId}`, phase: 2, status: 'pending' });
    }
    finals.push({ ...base, key: `s4:${s.reportId}`, phase: 4, status: 'pending' });
  }

  return [...independent, ...sources, ...dependent, ...finals, ...skipped];
}

function parseJson<T>(raw: string | null | undefined, fallback: T): T {
  if (!raw) return fallback;
  try { return JSON.parse(raw) as T; } catch { return fallback; }
}

/** Re-round ONE note (fresh hydrate → reset → re-pull Statement values → save). */
export async function reroundNote(engagementId: ID, scheduleId: ID, deps: ReroundDeps): Promise<void> {
  const h = await deps.hydrateNote(engagementId, scheduleId);
  const saved = parseJson<NoteWorkspaceData | null>(h.workspace?.data, null);
  if (!saved) throw new Error('No saved data');
  const ws0: NoteWorkspaceData = { ...saved, class_data: saved.class_data ?? {}, additional_disclosures: saved.additional_disclosures ?? {} };

  let ws = resetNoteRounding(ws0, {
    factor: h.roundingFactorKey,
    isCreditBalance: h.isCreditBalance,
    columnSpecJson: h.columnSpecJson,
    additionalDisclosuresSpecJson: h.additionalDisclosuresSpecJson,
    liveClasses: h.liveClasses ?? [],
  });
  // Statement-sourced figures take the Statement's SAVED values (rounded
  // included) — the Statement was re-saved earlier in this run.
  ws = refreshPlFlowLines(ws, h.inboundPlFlows ?? [], h.isCreditBalance, h.roundingFactorKey);
  const specs = parseJson<ValueSourceSpec[]>(h.additionalDisclosuresSpecJson, []);
  const updates = buildValueSourceRefresh(
    Array.isArray(specs) ? specs : [], ws.additional_disclosures,
    splitLiveValuesByDisclosure(h.valueSourceLiveValues), h.roundingFactorKey,
  );
  if (updates.length > 0) {
    const disc = { ...ws.additional_disclosures };
    for (const u of updates) disc[u.code] = u.rows;
    ws = { ...ws, additional_disclosures: disc };
  }
  await deps.saveNote(engagementId, scheduleId, { rowVersion: h.workspace?.rowVersion ?? null, data: JSON.stringify(ws) });
}

/** Refresh ONE Statement from its Notes (overrides kept) and save. Returns the kept overrides. */
export async function reroundStatement(engagementId: ID, reportId: ID, deps: ReroundDeps): Promise<string[]> {
  const h = await deps.hydrateStatement(engagementId, reportId);
  const saved = parseJson<StatementWorkspaceData | null>(h.workspace?.data, null);
  if (!saved) throw new Error('No saved data');
  const { workspace, keptOverrides } = refreshStatementFromLive(
    { ...saved, lines: saved.lines ?? {} }, h.liveEvaluation ?? {},
  );
  await deps.saveStatement(engagementId, reportId, { rowVersion: h.workspace?.rowVersion ?? null, data: JSON.stringify(workspace) });
  return keptOverrides;
}

/**
 * Runs the pending items in order, reporting each status change. Stops at
 * the first failure (later items may depend on it); call again to resume —
 * done / skipped items are not re-run.
 */
export async function runReround(
  engagementId: ID,
  items: ReroundItem[],
  deps: ReroundDeps,
  onChange: (items: ReroundItem[]) => void,
): Promise<ReroundItem[]> {
  let list = items.map(i => (i.status === 'failed' ? { ...i, status: 'pending' as const, reason: undefined } : i));
  const set = (key: string, patch: Partial<ReroundItem>) => {
    list = list.map(i => (i.key === key ? { ...i, ...patch } : i));
    onChange(list);
  };
  for (const item of list) {
    if (item.status !== 'pending') continue;
    set(item.key, { status: 'running' });
    try {
      if (item.kind === 'note') {
        await reroundNote(engagementId, item.id, deps);
        set(item.key, { status: 'done' });
      } else {
        const keptOverrides = await reroundStatement(engagementId, item.id, deps);
        set(item.key, { status: 'done', keptOverrides });
      }
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? (err instanceof Error ? err.message : 'Save failed');
      set(item.key, { status: 'failed', reason: msg });
      break;
    }
  }
  return list;
}
